import { ItineraryStop, catalogMeta, itineraryCityChain } from './circuits';
import { formatMoney } from './money';
import { CatalogEntry, CircuitOptional, CircuitProduct } from './queries';

/** Sellos comerciales del producto que el documento puede imprimir. */
export type CircuitPrintFlag = 'guaranteed' | 'onRequest' | 'passport';

/**
 * Una excursión opcional en el papel.
 *
 * SIN importe, y no por olvido: `optionals[].price` es el precio del PROVEEDOR
 * (así lo define el contrato del hub), y las excursiones se venden como extras
 * de la reserva — con proveedores de base neta, el precio al pasajero es ese
 * más el markup de la agencia, que hoy el backend todavía no resuelve para
 * adicionales. Imprimir el importe del proveedor en un documento que lee el
 * cliente le promete un precio más bajo del que va a pagar. El slot vuelve
 * cuando la API devuelva el importe retail ya resuelto.
 */
export interface CircuitPrintOptional {
  id: string;
  name: string;
  /** Ciudad y duración, ya en el orden en que se leen. */
  meta: string[];
  /** La marcó el asesor en la ficha: sale destacada. */
  selected: boolean;
}

export interface CircuitPrintDay {
  day: number;
  title?: string;
  /**
   * El relato del proveedor. Es SIEMPRE `description`: el `detail` extendido es
   * para la pantalla, donde se abre a pedido; en el papel multiplicaría por
   * cinco el largo de un documento que se manda por WhatsApp.
   */
  text?: string;
}

export interface CircuitPrintModel {
  /**
   * La marca del documento: la agencia, no el operador ni el producto. Es
   * OBLIGATORIA — un PDF sin marca ya salió mal, y una vez guardado o mandado
   * no se puede corregir. Por eso el tipo no la deja faltar y la acción de
   * imprimir está gateada por `clientPdfGate`.
   */
  agencyName: string;
  /** El asesor que cotiza, para que el cliente sepa a quién responderle. */
  advisor?: { name: string; contact: string[] };
  /** Fecha de generación: un precio impreso envejece sin avisar. */
  generatedAt: string;
  title: string;
  /** El operador va como texto, nunca como marca dominante. */
  operatedBy?: string;
  durationDays?: number;
  chain: { stops: ItineraryStop[]; withDays: boolean; truncated: boolean };
  flags: CircuitPrintFlag[];
  mapUrl?: string;
  includes: string[];
  /** El "desde" del catálogo, ya formateado. Indicativo por definición. */
  priceFrom?: string;
  days: CircuitPrintDay[];
  optionals: CircuitPrintOptional[];
}

export interface CircuitPrintInput {
  product: CircuitProduct;
  /** La entrada del catálogo que abrió la ficha: de ahí sale el "desde". */
  entry?: CatalogEntry;
  /** Ids de las excursiones que el asesor marcó. */
  selected: Set<string>;
  /** Nombre de la agencia seleccionada. Sin esto no se arma el documento. */
  agencyName: string;
  advisor?: { name: string; contact: string[] };
  /** Fecha ya formateada por el caller (el formato es del idioma). */
  generatedAt: string;
  /** Cómo se escribe la duración de un opcional en el idioma del asesor. */
  optionalDuration: (optional: CircuitOptional) => string | undefined;
}

/**
 * ¿Se puede generar el documento del cliente?
 *
 * La marca de la agencia es una invariante del papel, no un adorno: el asesor
 * puede guardar o reenviar el PDF en el mismo segundo en que se genera, así que
 * un documento sin marca no se arregla después. Mientras el nombre de la
 * agencia no esté, la acción se deshabilita y se DICE por qué —cargando o no
 * disponible— en vez de producir en silencio un PDF sin marca.
 */
export function clientPdfGate({
  agencyName,
  isFetching,
}: {
  agencyName?: string;
  isFetching: boolean;
}): { enabled: boolean; reason?: 'loading' | 'agencyUnavailable' } {
  if (agencyName?.trim()) return { enabled: true };
  return { enabled: false, reason: isFetching ? 'loading' : 'agencyUnavailable' };
}

/**
 * Todo lo que el PDF del circuito imprime, y NADA más.
 *
 * Es un modelo puro a propósito: el documento es RETAIL —lo lee el pasajero— y
 * la garantía de que no se le escapa un neto ni una comisión no puede depender
 * de revisar el JSX. Acá entra la oferta entera con su `pricing` (neto,
 * markup) y sale un modelo donde el único importe del circuito es el "desde"
 * de venta, ya formateado como texto: lo que no está en el modelo no se puede
 * imprimir.
 *
 * Por la misma razón NO hay total sumado circuito + opcionales: sumar plata es
 * del backend, y el total estimado se imprime recién cuando la API lo devuelva
 * resuelto (con el markup de adicionales).
 */
export function circuitPrintModel({
  product,
  entry,
  selected,
  agencyName,
  advisor,
  generatedAt,
  optionalDuration,
}: CircuitPrintInput): CircuitPrintModel {
  const meta = entry ? catalogMeta(entry) : undefined;
  const flags: CircuitPrintFlag[] = [
    ...(product.guaranteedDeparture ? (['guaranteed'] as const) : []),
    ...(product.immediateConfirmation === false ? (['onRequest'] as const) : []),
    ...(product.passportRequired ? (['passport'] as const) : []),
  ];

  const optionals = (product.optionals ?? []).map((optional) => ({
    id: optional.id,
    name: optional.name,
    meta: [optional.city, optionalDuration(optional)].filter(
      (item): item is string => Boolean(item),
    ),
    // `optional.price` NO se copia: ver `CircuitPrintOptional`.
    selected: selected.has(optional.id),
  }));

  return {
    agencyName,
    ...(advisor ? { advisor } : {}),
    generatedAt,
    title: product.name,
    ...(product.provider ? { operatedBy: product.provider } : {}),
    ...(product.durationDays ? { durationDays: product.durationDays } : {}),
    chain: itineraryCityChain(product.itinerary),
    flags,
    ...(product.images?.[0] ?? meta?.images?.[0]
      ? { mapUrl: product.images?.[0] ?? meta?.images?.[0] }
      : {}),
    includes: product.includes ?? [],
    // SOLO el precio de venta. `meta.pricing.net` existe en la entrada y se
    // queda afuera del modelo: es el margen de la agencia, no del pasajero.
    ...(meta?.pricing ? { priceFrom: formatMoney(meta.pricing.final) } : {}),
    days: (product.itinerary ?? []).map((day) => ({
      day: day.day,
      ...(day.title ? { title: day.title } : {}),
      ...(day.description ? { text: day.description } : {}),
    })),
    optionals: [
      // Las elegidas primero: el PDF vende la curaduría del asesor y después
      // muestra el menú, sin esconderlo.
      ...optionals.filter((optional) => optional.selected),
      ...optionals.filter((optional) => !optional.selected),
    ],
  };
}
